import React, { useCallback, useEffect, useState } from "react";
import { Button, Grid, Menu, MenuItem, TextField, Typography } from "@material-ui/core";
import {
    CartesianGrid,
    Cell,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import AccountCircleOutlinedIcon from '@material-ui/icons/AccountCircleOutlined';
import CalendarTodayOutlinedIcon from '@material-ui/icons/CalendarTodayOutlined';
import CreditCardOutlinedIcon from '@material-ui/icons/CreditCardOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import ExpandMoreOutlinedIcon from '@material-ui/icons/ExpandMoreOutlined';
import FilterListOutlinedIcon from '@material-ui/icons/FilterListOutlined';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import HistoryOutlinedIcon from '@material-ui/icons/HistoryOutlined';
import InsertChartOutlinedIcon from '@material-ui/icons/InsertChartOutlined';
import LinkOutlinedIcon from '@material-ui/icons/LinkOutlined';
import PeopleAltOutlinedIcon from '@material-ui/icons/PeopleAltOutlined';
import PhoneIphoneOutlinedIcon from '@material-ui/icons/PhoneIphoneOutlined';
import PublicOutlinedIcon from '@material-ui/icons/PublicOutlined';
import RefreshOutlinedIcon from '@material-ui/icons/RefreshOutlined';
import SettingsEthernetOutlinedIcon from '@material-ui/icons/SettingsEthernetOutlined';
import TimelineOutlinedIcon from '@material-ui/icons/TimelineOutlined';
import TrackChangesOutlinedIcon from '@material-ui/icons/TrackChangesOutlined';
import TrendingUpOutlinedIcon from '@material-ui/icons/TrendingUpOutlined';
import TopSpacer from "../../components/Common/spacer/TopSpacer";
import Breadcrums from "../../components/BreadCrums/breadcrums";
import SearchGrid from "../../components/table/SearchGrid";
import { GetUserInfo } from "../../Services/GetUserInfo";
import { PostBlobDataAPI, PostDataAPI } from "../../Services/APIService";
import { withSnackbar } from "../../components/Message/Alert";
import { formatDateByFormate } from "../../components/Common/Extensions";
import useStyles from "./styles";

const defaultSummaryData = {
    totalActivitiesCount: 0,
    totalLogins: 0,
    totalLogouts: 0,
    paymentActivity: 0,
    mdnSwapCount: 0,
    iccSwapCount: 0,
    imeiSwapCount: 0,
    totalUserActivity: 0,
};

const allUsersOption = { userId: null, label: "All users" };

const formatDateInputValue = (date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 15);

    return {
        startDate: formatDateInputValue(startDate),
        endDate: formatDateInputValue(endDate),
    };
};

function ActivityMonitoring({ ...props }) {
    const classes = useStyles();
    const { showMessage } = props;

    const [dateRange, setDateRange] = useState(getDefaultDateRange);
    const [selectedUser, setSelectedUser] = useState(allUsersOption);
    const [userOptions, setUserOptions] = useState([allUsersOption]);
    const [userMenuAnchor, setUserMenuAnchor] = useState(null);
    const [summaryData, setSummaryData] = useState(defaultSummaryData);
    const [isSummaryLoading, setIsSummaryLoading] = useState(false);
    const [isUsersLoading, setIsUsersLoading] = useState(false);
    const [moduleSummaryData, setModuleSummaryData] = useState([]);
    const [isModuleSummaryLoading, setIsModuleSummaryLoading] = useState(false);
    const [sessionActivityData, setSessionActivityData] = useState([]);
    const [isSessionsLoading, setIsSessionsLoading] = useState(false);
    const [sessionActivityDetails, setSessionActivityDetails] = useState([]);
    const [isActivityDetailsLoading, setIsActivityDetailsLoading] = useState(false);
    const [lineActivityHistoryData, setLineActivityHistoryData] = useState([]);
    const [isLineActivityHistoryLoading, setIsLineActivityHistoryLoading] = useState(false);
    const [isExportLoading, setIsExportLoading] = useState(false);

    const formatDateRangeLabel = (startDate, endDate) => {
        if (!startDate || !endDate)
            return "Select date range";

        const formatDatePart = (value, includeYear) => {
            const [year, month, day] = value.split("-").map(Number);
            const date = new Date(year, month - 1, day);
            return date.toLocaleDateString("en-US", {
                month: "short",
                day: "2-digit",
                ...(includeYear ? { year: "numeric" } : {}),
            });
        };

        const startYear = startDate.split("-")[0];
        const endYear = endDate.split("-")[0];
        return `${formatDatePart(startDate, startYear !== endYear)} – ${formatDatePart(endDate, true)}`;
    };

    const getLoggedInUser = useCallback(() => {
        const userInfoJson = GetUserInfo();

        if (!userInfoJson)
            return null;

        try {
            const userInfo = JSON.parse(userInfoJson);
            return userInfo?.user || null;
        } catch (error) {
            return null;
        }
    }, []);

    const getAssociateId = useCallback(() => {
        const loggedInUser = getLoggedInUser();
        const associateId = loggedInUser?.associateId;

        return associateId !== undefined && associateId !== null && associateId !== "" ? associateId : null;
    }, [getLoggedInUser]);

    const isValidDateRange = useCallback((range) => {
        if (!range.startDate) {
            showMessage("Error", "Please select start date", "error", 3000);
            return false;
        }

        if (!range.endDate) {
            showMessage("Error", "Please select end date", "error", 3000);
            return false;
        }

        if (range.startDate > range.endDate) {
            showMessage("Error", "Start date cannot be greater than end date", "error", 3000);
            return false;
        }

        return true;
    }, [showMessage]);

    const getActivityRequestParams = useCallback((range, userId) => {
        if (!isValidDateRange(range))
            return null;

        const associateId = getAssociateId();

        if (!associateId) {
            showMessage("Error", "Unable to load activity summary because associate id is missing.", "error", 3000);
            return null;
        }

        return {
            fromDate: `${range.startDate}T00:00:00`,
            toDate: `${range.endDate}T23:59:59`,
            userId: userId !== undefined && userId !== null && userId !== "" ? userId : null,
            customerId: associateId,
        };
    }, [getAssociateId, isValidDateRange, showMessage]);

    const getExportFileName = (headers, contentType) => {
        const disposition = headers?.["content-disposition"] || headers?.["Content-Disposition"] || "";
        const utfFileNameMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
        const fileNameMatch = disposition.match(/filename="?([^";]+)"?/i);

        if (utfFileNameMatch && utfFileNameMatch[1])
            return decodeURIComponent(utfFileNameMatch[1]);

        if (fileNameMatch && fileNameMatch[1])
            return fileNameMatch[1];

        return contentType && contentType.toLowerCase().indexOf("csv") > -1
            ? "activity-monitoring-export.csv"
            : "activity-monitoring-export.xlsx";
    };

    const downloadExportFile = (blob, fileName) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    const getBlobErrorMessage = async (blob) => {
        try {
            const errorText = await blob.text();
            const errorData = JSON.parse(errorText);

            return errorData?.message || "Unable to export activity dashboard. Please try again.";
        } catch (error) {
            return "Unable to export activity dashboard. Please try again.";
        }
    };

    const loadExecutiveSummary = useCallback((params) => {
        if (!params)
            return;

        setIsSummaryLoading(true);
        PostDataAPI("activityLogs/GetActivitySummary", params).then((result) => {
            setIsSummaryLoading(false);

            if (result.success && result.data != null) {
                setSummaryData({
                    ...defaultSummaryData,
                    ...result.data,
                });
            } else {
                showMessage("Error", result.message || "We're currently experiencing a temporary issue retrieving the activity summary. Please try again later.", "error", 3000);
            }
        }).catch(() => {
            setIsSummaryLoading(false);
            showMessage("Error", "We're currently experiencing a temporary issue retrieving the activity summary. Please try again later.", "error", 3000);
        });
    }, [showMessage]);

    const loadModuleWiseSummary = useCallback((params) => {
        if (!params)
            return;

        setIsModuleSummaryLoading(true);
        PostDataAPI("activityLogs/GetModuleWiseSummary", params).then((result) => {
            setIsModuleSummaryLoading(false);

            if (result.success && result.data != null) {
                setModuleSummaryData(result.data);
            } else {
                setModuleSummaryData([]);
                showMessage("Error", result.message || "We're currently experiencing a temporary issue retrieving the module-wise activity summary. Please try again later.", "error", 3000);
            }
        }).catch(() => {
            setIsModuleSummaryLoading(false);
            setModuleSummaryData([]);
            showMessage("Error", "We're currently experiencing a temporary issue retrieving the module-wise activity summary. Please try again later.", "error", 3000);
        });
    }, [showMessage]);

    const loadSessionActivityUserWise = useCallback((params) => {
        if (!params)
            return;

        setIsSessionsLoading(true);
        PostDataAPI("activityLogs/GetSessionActivityUserWise", params).then((result) => {
            setIsSessionsLoading(false);

            if (result.success && result.data != null) {
                setSessionActivityData(result.data);
            } else {
                setSessionActivityData([]);
                showMessage("Error", result.message || "We're currently experiencing a temporary issue retrieving sessions. Please try again later.", "error", 3000);
            }
        }).catch(() => {
            setIsSessionsLoading(false);
            setSessionActivityData([]);
            showMessage("Error", "We're currently experiencing a temporary issue retrieving sessions. Please try again later.", "error", 3000);
        });
    }, [showMessage]);

    const loadSessionActivityDetails = useCallback((params) => {
        if (!params)
            return;

        setIsActivityDetailsLoading(true);
        PostDataAPI("activityLogs/GetSessionActivityDetail", params).then((result) => {
            setIsActivityDetailsLoading(false);

            if (result.success && result.data != null) {
                const detailRows = Array.isArray(result.data) ? result.data : [];
                setSessionActivityDetails(detailRows);
            } else {
                setSessionActivityDetails([]);
                showMessage("Error", result.message || "We're currently experiencing a temporary issue retrieving activity details. Please try again later.", "error", 3000);
            }
        }).catch(() => {
            setIsActivityDetailsLoading(false);
            setSessionActivityDetails([]);
            showMessage("Error", "We're currently experiencing a temporary issue retrieving activity details. Please try again later.", "error", 3000);
        });
    }, [showMessage]);

    const loadLineActivityHistory = useCallback((params) => {
        if (!params)
            return;

        setIsLineActivityHistoryLoading(true);
        PostDataAPI("activityLogs/GetICCIDActivity", params).then((result) => {
            setIsLineActivityHistoryLoading(false);

            if (result.success && result.data != null) {
                setLineActivityHistoryData(Array.isArray(result.data) ? result.data : []);
            } else {
                setLineActivityHistoryData([]);
                showMessage("Error", result.message || "We're currently experiencing a temporary issue retrieving line activity history. Please try again later.", "error", 3000);
            }
        }).catch(() => {
            setIsLineActivityHistoryLoading(false);
            setLineActivityHistoryData([]);
            showMessage("Error", "We're currently experiencing a temporary issue retrieving line activity history. Please try again later.", "error", 3000);
        });
    }, [showMessage]);

    const loadActivityMonitoringData = useCallback((range, userId) => {
        const params = getActivityRequestParams(range, userId);

        if (!params)
            return;

        loadExecutiveSummary(params);
        loadModuleWiseSummary(params);
        loadSessionActivityUserWise(params);
        loadSessionActivityDetails(params);
        loadLineActivityHistory(params);
    }, [getActivityRequestParams, loadExecutiveSummary, loadLineActivityHistory, loadModuleWiseSummary, loadSessionActivityDetails, loadSessionActivityUserWise]);

    const loadUserOptions = useCallback(() => {
        const loggedInUser = getLoggedInUser();
        const associateId = loggedInUser?.associateId;
        const userId = loggedInUser?.userId;

        if (!associateId || !userId) {
            showMessage("Error", "Unable to load users because logged-in user information is missing.", "error", 3000);
            setUserOptions([allUsersOption]);
            return;
        }

        const params = {
            associateId: associateId,
            userId: userId,
        };

        setIsUsersLoading(true);
        PostDataAPI("fwuser/loadGrid", params).then((result) => {
            setIsUsersLoading(false);

            if (result.success && result.data != null && result.data.length > 0) {
                const loadedUsers = result.data.map((item) => {
                    const fullName = `${item.firstName || ""} ${item.lastName || ""}`.trim();

                    return {
                        userId: item.userId,
                        label: fullName || item.emailAddress || "User",
                    };
                });

                setUserOptions([
                    allUsersOption,
                    ...loadedUsers,
                ]);
            } else {
                setUserOptions([allUsersOption]);
            }
        }).catch(() => {
            setIsUsersLoading(false);
            setUserOptions([allUsersOption]);
            showMessage("Error", "We're currently experiencing a temporary issue retrieving users. Please try again later.", "error", 3000);
        });
    }, [getLoggedInUser, showMessage]);

    const handleApplyFilter = () => {
        loadActivityMonitoringData(dateRange, selectedUser.userId);
    };

    const handleUserMenuClose = () => {
        setUserMenuAnchor(null);
    };

    const handleUserSelect = (user) => {
        setSelectedUser(user);
        handleUserMenuClose();
    };

    const handleResetFilter = () => {
        const resetDateRange = getDefaultDateRange();

        setDateRange(resetDateRange);
        setSelectedUser(allUsersOption);
        setUserMenuAnchor(null);
        loadActivityMonitoringData(resetDateRange, null);
    };

    const handleExport = async () => {
        if (isExportLoading)
            return;

        const params = getActivityRequestParams(dateRange, selectedUser.userId);

        if (!params)
            return;

        setIsExportLoading(true);

        try {
            const response = await PostBlobDataAPI("activityLogs/ExportActivityDashboard", params);
            const contentType = response.headers?.["content-type"] || "application/octet-stream";
            const blob = new Blob([response.data], { type: contentType });

            if (contentType.toLowerCase().indexOf("application/json") > -1) {
                const errorMessage = await getBlobErrorMessage(blob);
                showMessage("Error", errorMessage, "error", 3000);
                return;
            }

            downloadExportFile(blob, getExportFileName(response.headers, contentType));
        } catch (error) {
            if (error?.response?.data instanceof Blob) {
                const errorMessage = await getBlobErrorMessage(error.response.data);
                showMessage("Error", errorMessage, "error", 3000);
            } else {
                showMessage("Error", "Unable to export activity dashboard. Please try again.", "error", 3000);
            }
        } finally {
            setIsExportLoading(false);
        }
    };

    useEffect(() => {
        const initialDateRange = getDefaultDateRange();

        loadUserOptions();
        loadActivityMonitoringData(initialDateRange, null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getSummaryValue = (value) => isSummaryLoading ? "Loading..." : value;
    const lineManagementActions = Number(summaryData.mdnSwapCount || 0) + Number(summaryData.iccSwapCount || 0) + Number(summaryData.imeiSwapCount || 0);

    const summaryCards = [
        { value: getSummaryValue(summaryData.totalLogins || 0), label: "Total Sessions", Icon: HistoryOutlinedIcon, background: "#DDF1FF", border: "#B8DFFD", iconBackground: "#1D84C6", iconColor: "#FFFFFF" },
        { value: getSummaryValue(summaryData.totalUserActivity || 0), label: "Unique Admins", Icon: PeopleAltOutlinedIcon, background: "#E9E9FF", border: "#D7D5FD", iconBackground: "#6758C9", iconColor: "#FFFFFF" },
        { value: getSummaryValue(0), label: "Reports Generated", Icon: DescriptionOutlinedIcon, background: "#FFF0D4", border: "#F4D9AA", iconBackground: "#EA9A3B", iconColor: "#14202F" },
        { value: getSummaryValue(summaryData.paymentActivity || 0), label: "Payment Activities", Icon: CreditCardOutlinedIcon, background: "#FCE6F0", border: "#EFC9DA", iconBackground: "#B46191", iconColor: "#FFFFFF" },
        { value: getSummaryValue(lineManagementActions), label: "Line Mgmt Actions", Icon: InsertChartOutlinedIcon, background: "#DDF1FF", border: "#B8DFFD", iconBackground: "#1D84C6", iconColor: "#FFFFFF" },
        { value: getSummaryValue(summaryData.iccSwapCount || 0), label: "SIM Swaps", Icon: PhoneIphoneOutlinedIcon, background: "#DDF5F2", border: "#BFE5E1", iconBackground: "#289A9E", iconColor: "#FFFFFF" },
        { value: getSummaryValue(summaryData.mdnSwapCount || 0), label: "MDN Swaps", Icon: SettingsEthernetOutlinedIcon, background: "#DDF6FF", border: "#BDE7F4", iconBackground: "#1D90A9", iconColor: "#FFFFFF" },
        { value: getSummaryValue(summaryData.imeiSwapCount || 0), label: "IMEI Swaps", Icon: LinkOutlinedIcon, background: "#E9E9FF", border: "#D7D5FD", iconBackground: "#6758C9", iconColor: "#FFFFFF" },
    ];

    const userActivitySummary = [
        { userName: "Craig Martin", initials: "CM", avatarClass: "blue", email: "craig.martin@figgers.com", sessions: 16, totalActivities: 412, profileUpdates: 28, reportsGenerated: 21, paymentActivities: 14, lineActions: 41, lastLogin: "Jun 10, 09:02 AM" },
        { userName: "Sarah Lee", initials: "SL", avatarClass: "purple", email: "sarah.lee@figgers.com", sessions: 13, totalActivities: 356, profileUpdates: 22, reportsGenerated: 19, paymentActivities: 12, lineActions: 30, lastLogin: "Jun 10, 02:18 PM" },
        { userName: "John Smith", initials: "JS", avatarClass: "teal", email: "john.smith@figgers.com", sessions: 11, totalActivities: 268, profileUpdates: 14, reportsGenerated: 13, paymentActivities: 9, lineActions: 28, lastLogin: "Jun 10, 09:00 AM" },
        { userName: "David Brown", initials: "DB", avatarClass: "orange", email: "david.brown@figgers.com", sessions: 8, totalActivities: 209, profileUpdates: 10, reportsGenerated: 10, paymentActivities: 7, lineActions: 19, lastLogin: "Jun 08, 01:12 PM" },
    ];

    const paymentHistoryAudit = [
        { dateTime: "May 01, 09:28 AM", admin: "Craig Martin", initials: "CM", avatarClass: "blue", activity: "Viewed paid invoices", ref: "—", amountDetail: "12 paid invoices listed" },
        { dateTime: "May 01, 09:29 AM", admin: "Craig Martin", initials: "CM", avatarClass: "blue", activity: "Viewed pending invoices", ref: "—", amountDetail: "2 pending invoices listed" },
        { dateTime: "May 01, 09:30 AM", admin: "Craig Martin", initials: "CM", avatarClass: "blue", activity: "Downloaded invoice", ref: "INV-10245", amountDetail: "invoice_INV-10245.pdf" },
        { dateTime: "May 09, 03:14 PM", admin: "Sarah Lee", initials: "SL", avatarClass: "purple", activity: "Payment attempted", ref: "PAY-55021", amountDetail: "$1,240.00 · Visa •••• 1234" },
        { dateTime: "May 09, 03:15 PM", admin: "Sarah Lee", initials: "SL", avatarClass: "purple", activity: "Payment completed", ref: "PAY-55021", amountDetail: "$1,240.00 · Visa •••• 1234" },
        { dateTime: "May 21, 10:02 AM", admin: "John Smith", initials: "JS", avatarClass: "teal", activity: "Payment failed", ref: "PAY-55310", amountDetail: "$860.00 · Mastercard •••• 8821" },
        { dateTime: "Jun 03, 04:26 PM", admin: "Craig Martin", initials: "CM", avatarClass: "blue", activity: "Card on file updated", ref: "—", amountDetail: "Visa •••• 1234 (brand + last 4 only)" },
    ];

    const profileActivityAudit = [
        { dateTime: "May 01, 09:18 AM", admin: "Craig Martin", initials: "CM", avatarClass: "blue", activity: "Viewed profile", oldValue: "—", newValue: "—" },
        { dateTime: "May 01, 09:20 AM", admin: "Craig Martin", initials: "CM", avatarClass: "blue", activity: "Updated card on file", oldValue: "Visa •••• 9876", newValue: "Visa •••• 1234" },
        { dateTime: "May 01, 09:22 AM", admin: "Craig Martin", initials: "CM", avatarClass: "blue", activity: "Updated shipping address", oldValue: "Miami, FL 33101", newValue: "Orlando, FL 32801" },
        { dateTime: "May 17, 01:35 PM", admin: "Sarah Lee", initials: "SL", avatarClass: "purple", activity: "Updated shipping address", oldValue: "Tampa, FL 33602", newValue: "Tampa, FL 33607" },
        { dateTime: "Jun 03, 04:26 PM", admin: "Craig Martin", initials: "CM", avatarClass: "blue", activity: "Updated card on file", oldValue: "Visa •••• 1234", newValue: "Visa •••• 4321" },
    ];

    const renderAdmin = (name, initials, avatarClass) => (
        <div className={classes.adminCell}>
            <span className={`${classes.adminAvatar} ${avatarClass}`}>{initials}</span>
            <span>{name}</span>
        </div>
    );

    const renderDevice = (device) => (
        <div className={classes.deviceCell}>
            <PublicOutlinedIcon />
            <span>{device}</span>
        </div>
    );

    const renderStrongNumber = (value) => (
        <span className={classes.strongNumber}>{value}</span>
    );

    const renderMono = (value) => (
        <span className={classes.monoText}>{value}</span>
    );

    const renderCompactMono = (value, titleValue) => (
        <span className={classes.compactMonoText} title={titleValue || value}>
            {value}
        </span>
    );

    const renderMuted = (value) => (
        <span className={classes.mutedText}>{value}</span>
    );

    const isEmptyActivityValue = (value) => (
        value === null || value === undefined || value === "" || value === "-"
    );

    const getCleanActivityValue = (value) => (
        isEmptyActivityValue(value) ? "" : value.toString().trim()
    );

    const getCompactActivityValue = (value) => {
        const cleanValue = getCleanActivityValue(value);

        if (!cleanValue)
            return "—";

        if (cleanValue.length <= 18)
            return cleanValue;

        return `${cleanValue.substring(0, 8)}...${cleanValue.substring(cleanValue.length - 6)}`;
    };

    const getActivityLabel = (activity) => {
        const activityLabels = {
            CARRIER_CHANGED: "Carrier Changed",
            ICC_SWAP: "ICC Swap",
            IMEI_SWAP: "IMEI Swap",
            LINE_ACTIVATED: "Line Activated",
            LINE_DISCONNECTED: "Line Disconnected",
            LINE_NAME_CHANGED: "Line Name Changed",
            LINE_RECONNECTED: "Line Reconnected",
            LINE_SUSPENDED: "Line Suspended",
            MDN_SWAP: "MDN Swap",
        };

        if (!activity)
            return "—";

        return activityLabels[activity] || activity.toString().toLowerCase().split("_").map((word) => (
            word.charAt(0).toUpperCase() + word.slice(1)
        )).join(" ");
    };

    const getActivityBadgeClass = (activity) => {
        if (["ICC_SWAP", "IMEI_SWAP", "MDN_SWAP"].indexOf(activity) > -1)
            return classes.activityBadgeSwap;

        if (["LINE_ACTIVATED", "LINE_DISCONNECTED", "LINE_RECONNECTED", "LINE_SUSPENDED"].indexOf(activity) > -1)
            return classes.activityBadgeStatus;

        return classes.activityBadgeChange;
    };

    const renderActivityBadge = (activity) => (
        <span className={`${classes.activityBadge} ${getActivityBadgeClass(activity)}`}>
            {getActivityLabel(activity)}
        </span>
    );

    const getStatusChangeDisplayValue = (beforeValue, afterValue) => {
        const before = getCleanActivityValue(beforeValue);
        const after = getCleanActivityValue(afterValue);

        if (before && after)
            return `${before} → ${after}`;

        if (!before && after)
            return `— → ${after}`;

        return "—";
    };

    const getCompactChangeDisplay = (beforeValue, afterValue) => {
        const before = getCleanActivityValue(beforeValue);
        const after = getCleanActivityValue(afterValue);

        if (!before || !after)
            return {
                display: "—",
                title: "—",
            };

        return {
            display: `${getCompactActivityValue(before)} → ${getCompactActivityValue(after)}`,
            title: `${before} → ${after}`,
        };
    };

    const renderTruncated = (value, maxLength = 40) => {
        const displayValue = value || "—";
        const truncatedValue = displayValue.length > maxLength ? `${displayValue.substring(0, maxLength)}...` : displayValue;

        return (
            <span className={classes.truncatedText} title={displayValue}>
                {truncatedValue}
            </span>
        );
    };

    const getInitials = (name) => {
        if (!name)
            return "";

        return name
            .split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part.charAt(0).toUpperCase())
            .join("");
    };

    const getSessionGroupColor = (index) => {
        const colors = ["blue", "teal", "purple", "green"];
        return colors[index % colors.length];
    };

    const parseUserAgent = (userAgent) => {
        if (!userAgent)
            return "Browser / Device";

        const isChrome = userAgent.indexOf("Chrome") > -1 || userAgent.indexOf("CriOS") > -1;
        const isEdge = userAgent.indexOf("Edg") > -1;
        const isSafari = userAgent.indexOf("Safari") > -1 && !isChrome && !isEdge;
        const isFirefox = userAgent.indexOf("Firefox") > -1;
        const isWindows = userAgent.indexOf("Windows") > -1;
        const isMac = userAgent.indexOf("Macintosh") > -1 || userAgent.indexOf("Mac OS") > -1;
        const isAndroid = userAgent.indexOf("Android") > -1;
        const isiOS = userAgent.indexOf("iPhone") > -1 || userAgent.indexOf("iPad") > -1;

        let browser = "Browser";
        let device = "Device";

        if (isEdge)
            browser = "Edge";
        else if (isChrome)
            browser = "Chrome";
        else if (isSafari)
            browser = "Safari";
        else if (isFirefox)
            browser = "Firefox";

        if (isWindows)
            device = "Windows";
        else if (isMac)
            device = "macOS";
        else if (isAndroid)
            device = "Android";
        else if (isiOS)
            device = "iOS";

        return `${browser} / ${device}`;
    };

    const getSessionDuration = (loginTime, logOutTime) => {
        if (!loginTime || !logOutTime)
            return "—";

        const loginDate = new Date(loginTime);
        const logoutDate = new Date(logOutTime);

        if (Number.isNaN(loginDate.getTime()) || Number.isNaN(logoutDate.getTime()) || logoutDate < loginDate)
            return "—";

        const totalMinutes = Math.max(0, Math.round((logoutDate.getTime() - loginDate.getTime()) / 60000));
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;

        if (hours > 0 && minutes > 0)
            return `${hours} hr ${minutes} min`;

        if (hours > 0)
            return `${hours} hr`;

        return `${totalMinutes} min`;
    };

    const getSessionDateKey = (session) => (
        session.createDate ? formatDateByFormate(session.createDate, "YYYY-MM-DD") : "unknown"
    );

    const moduleChartColors = ["#1F7EBA", "#2B9A9D", "#278D58", "#D98137", "#665ACD"];

    const dailyActivityTrend = Object.values(sessionActivityDetails.reduce((groups, activityItem) => {
        if (!activityItem.activityCreateDate)
            return groups;

        const dateKey = formatDateByFormate(activityItem.activityCreateDate, "YYYY-MM-DD");

        if (!groups[dateKey]) {
            groups[dateKey] = {
                dateKey,
                date: formatDateByFormate(activityItem.activityCreateDate, "MMM DD"),
                activities: 0,
            };
        }

        groups[dateKey].activities += 1;
        return groups;
    }, {})).sort((firstItem, secondItem) => firstItem.dateKey.localeCompare(secondItem.dateKey));

    const activitiesByModuleCounts = sessionActivityDetails.reduce((counts, activityItem) => {
        const moduleName = (activityItem.area || "Unknown").trim();

        counts[moduleName] = (counts[moduleName] || 0) + 1;
        return counts;
    }, {});

    const activitiesByModuleChartData = Object.entries(activitiesByModuleCounts)
        .map(([moduleName, count]) => ({
            name: moduleName,
            value: count,
        }))
        .sort((firstItem, secondItem) => secondItem.value - firstItem.value)
        .slice(0, 5)
        .map((item, index) => ({
            ...item,
            color: moduleChartColors[index % moduleChartColors.length],
        }));

    const maxDailyActivities = dailyActivityTrend.reduce((maxValue, item) => Math.max(maxValue, item.activities), 0);
    const dailyChartMax = Math.max(5, Math.ceil(maxDailyActivities / 5) * 5);

    const renderModuleName = (moduleName) => {
        const moduleIcons = {
            Authentication: <TrackChangesOutlinedIcon />,
            Profile: <AccountCircleOutlinedIcon />,
            "Account Usage": <TimelineOutlinedIcon />,
            Reports: <DescriptionOutlinedIcon />,
            "Payment History": <CreditCardOutlinedIcon />,
            "Lines Management": <PhoneIphoneOutlinedIcon />,
        };

        return (
            <span className={classes.moduleNameCell}>
                <span className={classes.moduleNameIcon}>{moduleIcons[moduleName] || <TrackChangesOutlinedIcon />}</span>
                <span>{moduleName}</span>
            </span>
        );
    };

    const sessionGridGroups = Object.values(sessionActivityData.reduce((groups, session) => {
        const dateKey = getSessionDateKey(session);

        if (!groups[dateKey]) {
            groups[dateKey] = {
                dateKey,
                date: dateKey === "unknown" ? "Unknown date" : formatDateByFormate(session.createDate, "MMM DD, YYYY"),
                rows: [],
            };
        }

        groups[dateKey].rows.push(session);
        return groups;
    }, {})).sort((firstGroup, secondGroup) => (
        secondGroup.dateKey.localeCompare(firstGroup.dateKey)
    )).map((group, groupIndex) => {
        const activities = group.rows.reduce((total, row) => total + Number(row.activityCount || 0), 0);
        return {
            ...group,
            sessions: group.rows.length,
            activities,
            colorClass: getSessionGroupColor(groupIndex),
            rows: group.rows.map((row, rowIndex) => {
                const userName = row.userName || "User";
                const activityCount = Number(row.activityCount || 0);

                return {
                    id: row.sessionLogId || `${group.dateKey}-${rowIndex}`,
                    ...row,
                    loginTime: row.loginTime ? formatDateByFormate(row.loginTime, "hh:mm A") : "—",
                    logoutTime: row.logOutTime ? formatDateByFormate(row.logOutTime, "hh:mm A") : "—",
                    admin: userName,
                    adminHTML: renderAdmin(userName, getInitials(userName), "teal"),
                    duration: getSessionDuration(row.loginTime, row.logOutTime),
                    activities: activityCount,
                    activitiesHTML: renderStrongNumber(activityCount),
                    device: parseUserAgent(row.userAgent),
                    deviceHTML: renderDevice(parseUserAgent(row.userAgent)),
                    ipAddress: row.ipAddress || "—",
                };
            }),
        };
    });

    const lineActivityHistoryRows = lineActivityHistoryData.map((row, index) => {
        const lineIccid = getCleanActivityValue(row.iccidAfter) || getCleanActivityValue(row.iccidBefore);
        const activityLabel = getActivityLabel(row.activity);
        const mdnChange = getCompactChangeDisplay(row.mdnBefore, row.mdnAfter);
        const iccidChange = getCompactChangeDisplay(row.iccidBefore, row.iccidAfter);
        const imeiChange = getCompactChangeDisplay(row.imeiBefore, row.imeiAfter);
        const statusChange = getStatusChangeDisplayValue(row.lineStatusBefore, row.lineStatusAfter);

        return {
            id: row.activityLogId || row.session || `${row.activityDate || "line-activity"}-${index}`,
            ...row,
            dateTime: row.activityDate ? formatDateByFormate(row.activityDate, "MMM DD, hh:mm A") : "—",
            activityLabel,
            activityHTML: renderActivityBadge(row.activity),
            lineIccid: lineIccid || "—",
            lineIccidHTML: renderCompactMono(getCompactActivityValue(lineIccid), lineIccid || "—"),
            mdnChange: mdnChange.title,
            mdnChangeHTML: renderCompactMono(mdnChange.display, mdnChange.title),
            iccidChange: iccidChange.title,
            iccidChangeHTML: renderCompactMono(iccidChange.display, iccidChange.title),
            imeiChange: imeiChange.title,
            imeiChangeHTML: renderCompactMono(imeiChange.display, imeiChange.title),
            statusChange,
            statusChangeHTML: renderMono(statusChange),
        };
    });

    const moduleActivitySummaryRows = moduleSummaryData.map((row, index) => ({
        id: `module-summary-${index}`,
        ...row,
        moduleHTML: renderModuleName(row.module),
        totalActivitiesHTML: renderStrongNumber(row.totalActivities),
        lastActivityTime: row.lastActivityTime ? formatDateByFormate(row.lastActivityTime, "MMM DD, hh:mm A") : "",
        mostActiveAdminHTML: row.mostActiveAdmin ? renderAdmin(row.mostActiveAdmin, getInitials(row.mostActiveAdmin), "teal") : "",
    }));

    const userActivitySummaryRows = userActivitySummary.map((row, index) => ({
        id: `user-summary-${index}`,
        ...row,
        userNameHTML: renderAdmin(row.userName, row.initials, row.avatarClass),
        totalActivitiesHTML: renderStrongNumber(row.totalActivities),
    }));

    const paymentHistoryRows = paymentHistoryAudit.map((row, index) => ({
        id: `payment-${index}`,
        ...row,
        adminHTML: renderAdmin(row.admin, row.initials, row.avatarClass),
        refHTML: renderMono(row.ref),
        amountDetailHTML: renderMono(row.amountDetail),
    }));

    const profileActivityRows = profileActivityAudit.map((row, index) => ({
        id: `profile-${index}`,
        ...row,
        adminHTML: renderAdmin(row.admin, row.initials, row.avatarClass),
        oldValueHTML: renderMono(row.oldValue),
        newValueHTML: renderMono(row.newValue),
    }));

    const fullActivityLogRows = sessionActivityDetails.map((row, index) => {
        const userName = row.userName || "User";
        const sessionId = row.sessionId || "—";
        const shortSessionId = sessionId !== "—" && sessionId.length > 12 ? `${sessionId.substring(0, 12)}...` : sessionId;

        return {
            id: row.activityLogId || `${row.sessionLogId || "activity"}-${index}`,
            ...row,
            dateTime: row.activityCreateDate ? formatDateByFormate(row.activityCreateDate, "MMM DD, hh:mm A") : "—",
            sessionId,
            sessionIdHTML: <span className={classes.monoText} title={sessionId}>{shortSessionId}</span>,
            admin: userName,
            adminHTML: renderAdmin(userName, getInitials(userName), "teal"),
            module: (row.area || "Unknown").trim(),
            activity: row.activity || "—",
            details: row.details || "—",
            detailsHTML: renderTruncated(row.details, 60),
            ipAddress: row.ipAddress || "—",
            ipAddressHTML: renderMono(row.ipAddress || "—"),
            status: row.activityStatus || "—",
            statusHTML: renderMuted(row.activityStatus || "—"),
        };
    });

    return (
        <>
            <TopSpacer />

            <div className={classes.header}>
                <Grid container alignItems="center">
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                        <Breadcrums parentLink={"Corporate"} currentLink={"Activity Monitoring"} />
                    </Grid>
                </Grid>
            </div>

            <div className={classes.pageContent}>
                <div className={classes.filterBar}>
                    <TextField
                        label="Start Date"
                        type="date"
                        variant="outlined"
                        size="small"
                        value={dateRange.startDate}
                        onChange={(event) => setDateRange((previousValue) => ({
                            ...previousValue,
                            startDate: event.target.value,
                        }))}
                        InputLabelProps={{ shrink: true }}
                        className={`${classes.customDateField} ${classes.filterDateField}`}
                    />
                    <TextField
                        label="End Date"
                        type="date"
                        variant="outlined"
                        size="small"
                        value={dateRange.endDate}
                        onChange={(event) => setDateRange((previousValue) => ({
                            ...previousValue,
                            endDate: event.target.value,
                        }))}
                        InputLabelProps={{ shrink: true }}
                        className={`${classes.customDateField} ${classes.filterDateField}`}
                    />

                    <button
                        type="button"
                        className={`${classes.filterPill} ${classes.clickableFilterPill}`}
                        onClick={(event) => setUserMenuAnchor(event.currentTarget)}
                        disabled={isUsersLoading}
                    >
                        <AccountCircleOutlinedIcon className={classes.filterIcon} />
                        <div className={classes.filterText}>
                            <Typography className={classes.filterLabel}>User</Typography>
                            <Typography className={classes.filterValue}>{isUsersLoading ? "Loading users..." : selectedUser.label}</Typography>
                        </div>
                        <ExpandMoreOutlinedIcon className={classes.filterArrow} />
                    </button>

                    <Button className={classes.applyButton} startIcon={<FilterListOutlinedIcon />} onClick={handleApplyFilter}>
                        Apply Filter
                    </Button>
                    <Button className={classes.resetButton} startIcon={<RefreshOutlinedIcon />} onClick={handleResetFilter}>
                        Reset
                    </Button>
                    <Button
                        className={classes.exportButton}
                        startIcon={<GetAppOutlinedIcon />}
                        onClick={handleExport}
                        disabled={isExportLoading}
                    >
                        {isExportLoading ? "Exporting..." : "Export"}
                    </Button>

                    <Menu
                        anchorEl={userMenuAnchor}
                        open={Boolean(userMenuAnchor)}
                        onClose={handleUserMenuClose}
                        getContentAnchorEl={null}
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                        transformOrigin={{ vertical: "top", horizontal: "left" }}
                        classes={{ paper: classes.filterMenuPaper }}
                    >
                        {userOptions.map((user) => (
                            <MenuItem
                                key={user.userId === null ? "all-users" : user.userId}
                                className={classes.filterMenuItem}
                                selected={selectedUser.userId === user.userId}
                                onClick={() => handleUserSelect(user)}
                            >
                                {user.label}
                            </MenuItem>
                        ))}
                    </Menu>
                </div>

                <div className={classes.summaryHeader}>
                    <span className={classes.summaryHeaderIcon}>
                        <TrendingUpOutlinedIcon />
                    </span>
                    <Typography className={classes.summaryTitle}>
                        Executive Summary
                    </Typography>
                    <Typography className={classes.summaryDate}>
                        · {formatDateRangeLabel(dateRange.startDate, dateRange.endDate)}
                    </Typography>
                </div>

                <div className={classes.summaryGrid}>
                    {summaryCards.map((card) => {
                        const CardIcon = card.Icon;

                        return (
                            <div
                                className={classes.summaryCard}
                                key={card.label}
                                style={{
                                    backgroundColor: card.background,
                                    borderColor: card.border,
                                }}
                            >
                                <span
                                    className={classes.cardIcon}
                                    style={{
                                        backgroundColor: card.iconBackground,
                                        color: card.iconColor,
                                    }}
                                >
                                    <CardIcon />
                                </span>
                                <div>
                                    <Typography className={classes.cardValue}>{card.value}</Typography>
                                    <Typography className={classes.cardLabel}>{card.label}</Typography>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className={classes.insightsSection}>
                    <div className={classes.insightsHeader}>
                        <span className={classes.insightsHeaderIcon}>
                            <InsertChartOutlinedIcon />
                        </span>
                        <Typography className={classes.insightsTitle}>
                            Activity Insights
                        </Typography>
                        <Typography className={classes.insightsSubtitle}>
                            · Compact charts for the selected date range
                        </Typography>
                    </div>

                    <div className={classes.insightsCompactGrid}>
                        <div className={`${classes.chartCard} ${classes.trendChartCard} ${classes.chartCardEnter} ${classes.chartDelayOne}`}>
                            <div className={classes.chartCardHeader}>
                                <Typography className={classes.chartTitle}>
                                    <TimelineOutlinedIcon />
                                    Daily Activity Trend
                                </Typography>
                                <Typography className={classes.chartSubtitle}>
                                    Total activities per day — spot spikes and unusually busy days.
                                </Typography>
                            </div>

                            <div className={`${classes.lineChartWrap} ${classes.lineChartAnimated}`}>
                                {isActivityDetailsLoading ? (
                                    <Typography className={classes.moduleLoadingText}>Loading activity insights...</Typography>
                                ) : dailyActivityTrend.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={180}>
                                        <LineChart data={dailyActivityTrend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                                            <CartesianGrid stroke="#DDE9F2" strokeDasharray="4 4" vertical={true} />
                                            <XAxis
                                                dataKey="date"
                                                interval={dailyActivityTrend.length > 8 ? 1 : 0}
                                                axisLine={{ stroke: "#D7E2EA" }}
                                                tickLine={false}
                                                tick={{ fill: "#6A7A8D", fontSize: 10 }}
                                            />
                                            <YAxis
                                                domain={[0, dailyChartMax]}
                                                ticks={[0, Math.ceil(dailyChartMax / 2), dailyChartMax]}
                                                axisLine={false}
                                                tickLine={false}
                                                tick={{ fill: "#6A7A8D", fontSize: 10 }}
                                                width={34}
                                            />
                                            <Tooltip
                                                contentStyle={{ borderRadius: 8, borderColor: "#C9DCEB", fontFamily: "Lato", fontSize: 12 }}
                                                formatter={(value) => [`${value} activities`, "Total"]}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="activities"
                                                stroke="#0B78C9"
                                                strokeWidth={2}
                                                dot={false}
                                                isAnimationActive={false}
                                                activeDot={{ r: 4, fill: "#0B78C9", stroke: "#FFFFFF", strokeWidth: 2 }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <Typography className={classes.moduleLoadingText}>No activity insights found.</Typography>
                                )}
                            </div>
                        </div>

                        <div className={`${classes.chartCard} ${classes.moduleChartCard} ${classes.chartCardEnter} ${classes.chartDelayTwo}`}>
                            <div className={classes.chartCardHeader}>
                                <Typography className={classes.chartTitle}>
                                    <TrackChangesOutlinedIcon />
                                    Activities by Module
                                </Typography>
                                <Typography className={classes.chartSubtitle}>
                                    Which portal areas were used most during the period.
                                </Typography>
                            </div>

                            <div className={`${classes.donutChartBody} ${classes.donutChartAnimated}`}>
                                {isActivityDetailsLoading ? (
                                    <Typography className={classes.moduleLoadingText}>Loading activity insights...</Typography>
                                ) : activitiesByModuleChartData.length > 0 ? (
                                    <>
                                        <div className={classes.donutChart}>
                                            <ResponsiveContainer width="100%" height={180}>
                                                <PieChart>
                                                    <Pie
                                                        data={activitiesByModuleChartData}
                                                        dataKey="value"
                                                        nameKey="name"
                                                        innerRadius={54}
                                                        outerRadius={84}
                                                        paddingAngle={1}
                                                        stroke="#FFFFFF"
                                                        strokeWidth={2}
                                                        isAnimationActive={false}
                                                    >
                                                        {activitiesByModuleChartData.map((item) => (
                                                            <Cell key={item.name} fill={item.color} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip
                                                        contentStyle={{ borderRadius: 8, borderColor: "#C9DCEB", fontFamily: "Lato", fontSize: 12 }}
                                                        formatter={(value) => [`${value}`, "Activities"]}
                                                    />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>

                                        <div className={`${classes.moduleLegend} ${classes.legendAnimated}`}>
                                            {activitiesByModuleChartData.map((item) => (
                                                <div className={classes.legendRow} key={item.name}>
                                                    <span className={classes.legendDot} style={{ backgroundColor: item.color }} />
                                                    <span className={classes.legendName}>{item.name}</span>
                                                    <span className={classes.legendValue}>{item.value}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <Typography className={classes.moduleLoadingText}>No module activity found.</Typography>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={classes.sessionsSection}>
                    <div className={classes.sessionsHeader}>
                        <span className={classes.sessionsHeaderIcon}>
                            <HistoryOutlinedIcon />
                        </span>
                        <Typography className={classes.sessionsTitle}>
                            Sessions by Date
                        </Typography>
                        <Typography className={classes.sessionsSubtitle}>
                            · {sessionActivityData.length} sessions grouped by day across the selected range
                        </Typography>
                    </div>

                    {isSessionsLoading ? (
                        <Typography className={classes.moduleLoadingText}>Loading sessions...</Typography>
                    ) : (
                        <>
                            {sessionGridGroups.length > 0 ? sessionGridGroups.map((group) => (
                                <div className={classes.dateGroupCard} key={group.dateKey}>
                                    <div className={`${classes.dateGroupHeader} ${group.colorClass}`}>
                                        <div className={classes.dateGroupTitle}>
                                            <CalendarTodayOutlinedIcon />
                                            <Typography>{group.date}</Typography>
                                        </div>
                                        <div className={classes.dateGroupStats}>
                                            <span><strong>{group.sessions}</strong> sessions</span>
                                            <span><strong>{group.activities}</strong> activities</span>
                                        </div>
                                    </div>

                                    <div className={`${classes.auditGrid} session`}>
                                        <SearchGrid
                                            columns="auditSessionsByDate"
                                            list={group.rows}
                                            isUpdate={group.dateKey}
                                            isSearchAble={false}
                                            gridPadding={true}
                                            defaultListLength={50}
                                            noRecordMsg="No sessions found for selected filters."
                                        />
                                    </div>
                                </div>
                            )) : (
                                <Typography className={classes.moduleLoadingText}>No sessions found for selected filters.</Typography>
                            )}
                        </>
                    )}
                </div>

                <div className={`${classes.detailSection} moduleSummary`}>
                    <div className={classes.detailHeader}>
                        <span className={classes.detailHeaderIcon}>
                            <TrackChangesOutlinedIcon />
                        </span>
                        <Typography className={classes.detailTitle}>
                            Module-wise Activity Summary
                        </Typography>
                        <Typography className={classes.detailSubtitle}>
                            · Module totals for the selected date range
                        </Typography>
                    </div>

                    <div className={classes.detailTableCard}>
                        {isModuleSummaryLoading ? (
                            <Typography className={classes.moduleLoadingText}>Loading module activity...</Typography>
                        ) : (
                            <div className={`${classes.auditGrid} ${classes.summaryAuditGrid}`}>
                                <SearchGrid
                                    columns="auditModuleActivitySummary"
                                    list={moduleActivitySummaryRows}
                                    isUpdate={`module-activity-summary-${moduleActivitySummaryRows.length}`}
                                    isSearchAble={false}
                                    gridPadding={true}
                                    defaultListLength={50}
                                    noRecordMsg="No module activity found."
                                />
                            </div>
                        )}
                    </div>
                </div>

           

                <div className={`${classes.detailSection} line`}>
                    <div className={classes.detailHeader}>
                        <span className={classes.detailHeaderIcon}>
                            <PhoneIphoneOutlinedIcon />
                        </span>
                        <Typography className={classes.detailTitle}>
                            Line Activity History
                        </Typography>
                        <Typography className={classes.detailSubtitle}>
                            · ICCID, MDN, IMEI, line status, carrier and line name changes
                        </Typography>
                    </div>

                    <div className={classes.detailTableCard}>
                        {isLineActivityHistoryLoading ? (
                            <Typography className={classes.moduleLoadingText}>Loading line activity...</Typography>
                        ) : (
                            <div className={`${classes.auditGrid} ${classes.lineHistoryGrid}`}>
                                <SearchGrid
                                    columns="auditLineActivityHistory"
                                    list={lineActivityHistoryRows}
                                    isUpdate={`line-activity-history-${lineActivityHistoryRows.length}`}
                                    isSearchAble={false}
                                    gridPadding={true}
                                    defaultListLength={50}
                                    noRecordMsg="No line activity found."
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* <div className={`${classes.detailSection} payment`}>
                    <div className={classes.detailHeader}>
                        <span className={classes.detailHeaderIcon}>
                            <CreditCardOutlinedIcon />
                        </span>
                        <Typography className={classes.detailTitle}>
                            Payment History Audit
                        </Typography>
                        <Typography className={classes.detailSubtitle}>
                            · Invoice and payment activity · card numbers masked
                        </Typography>
                    </div>

                    <div className={classes.detailTableCard}>
                        <div className={classes.auditGrid}>
                            <SearchGrid
                                columns="auditPaymentHistoryAudit"
                                list={paymentHistoryRows}
                                isUpdate="payment-history-audit"
                                isSearchAble={false}
                                gridPadding={true}
                                defaultListLength={50}
                            />
                        </div>
                    </div>
                </div> */}

                {/* <div className={`${classes.detailSection} profile`}>
                    <div className={classes.detailHeader}>
                        <span className={classes.detailHeaderIcon}>
                            <AccountCircleOutlinedIcon />
                        </span>
                        <Typography className={classes.detailTitle}>
                            Profile Activity Audit
                        </Typography>
                        <Typography className={classes.detailSubtitle}>
                            · Profile views and updates · city/state/zip and card last-4 only
                        </Typography>
                    </div>

                    <div className={classes.detailTableCard}>
                        <div className={classes.auditGrid}>
                            <SearchGrid
                                columns="auditProfileActivityAudit"
                                list={profileActivityRows}
                                isUpdate="profile-activity-audit"
                                isSearchAble={false}
                                gridPadding={true}
                                defaultListLength={50}
                            />
                        </div>
                    </div>
                </div> */}

                <div className={`${classes.detailSection} fullLog`}>
                    <div className={classes.detailHeader}>
                        <span className={classes.detailHeaderIcon}>
                            <HistoryOutlinedIcon />
                        </span>
                        <Typography className={classes.detailTitle}>
                            Full Activity Log
                        </Typography>
                        <Typography className={classes.detailSubtitle}>
                            · Complete audit trail · {sessionActivityDetails.length} activities
                        </Typography>
                        <Typography className={classes.detailHeaderCount}>
                            Showing {fullActivityLogRows.length} of {sessionActivityDetails.length}
                        </Typography>
                    </div>

                    <div className={classes.detailTableCard}>
                        {isActivityDetailsLoading ? (
                            <Typography className={classes.moduleLoadingText}>Loading activity logs...</Typography>
                        ) : (
                            <div className={classes.auditGrid}>
                                <SearchGrid
                                    columns="auditFullActivityLog"
                                    list={fullActivityLogRows}
                                    isUpdate={`full-activity-log-${fullActivityLogRows.length}`}
                                    isSearchAble={false}
                                    gridPadding={true}
                                    defaultListLength={50}
                                    noRecordMsg="No activity logs found."
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default withSnackbar(ActivityMonitoring);
